import React from "react";
import {
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';

export const CervicalDiagnosisTab = ({
    facilityType,
    isDhOrChc,
    formData,
    diagnosisAction,
    setDiagnosisAction,
    doctorsObservation,
    setDoctorsObservation,
    dhDiagnosisData,
    setDhDiagnosisData,
    dhErrors,
    setDhErrors,
    handleSaveDiagnosis,
    setActiveWorkflowTab,
    screeningStatus,
    handleOpenUpdateModal,
    isDiagnosed,
    patientDiagnosis,
    isCancerConfirmedLocked,
    isNad,
    cryotherapyRecommended,
    referralRecommended,
    managementRecommended
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Status and Quick Links container */}
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #eef2f5', bgcolor: '#fafafa', mb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '15px' }}>
                            Status: {screeningStatus || ''}
                        </Typography>
                        {!isDhOrChc && handleOpenUpdateModal && (
                            <Button
                                variant="text"
                                size="small"
                                sx={{ color: '#3f51b5', fontWeight: 700, minWidth: 'auto', p: 0, textTransform: 'none', textDecoration: 'underline' }}
                                onClick={handleOpenUpdateModal}
                            >
                                Update
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#3f51b5',
                            color: '#fff',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            px: 0.5,
                            py: 0.2,
                            borderRadius: '2px',
                            lineHeight: 1
                        }}>
                            PDF
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#3f51b5', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                            Quick Links
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* FOLLOW UP Card */}
                    <Box
                        onClick={() => setActiveWorkflowTab && setActiveWorkflowTab(3)}
                        sx={{
                            width: '95px',
                            height: '80px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: '#fff',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#3f51b5' }
                        }}
                    >
                        <img
                            src="/images/Foloowup.png"
                            alt="FOLLOW UP"
                            style={{ width: '32px', height: '32px', marginBottom: '4px', objectFit: 'contain' }}
                        />
                        <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#3f51b5' }}>
                            FOLLOW UP
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Patient Diagnosis Summary Banner if already diagnosed (PHC mode only) */}
            {!isDhOrChc && isDiagnosed && patientDiagnosis && (
                <Box sx={{ bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '4px', p: 2, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#3730a3', mb: 0.5 }}>
                        Patient Diagnosis:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4338ca', fontWeight: 600 }}>
                        {patientDiagnosis}
                    </Typography>
                </Box>
            )}

            {/* Diagnosis Main Form Container */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', p: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Diagnosis
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            variant="standard"
                            value={dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                            InputProps={{
                                readOnly: true,
                                endAdornment: <CalendarTodayIcon sx={{ fontSize: '18px', color: '#64748b', ml: 1 }} />
                            }}
                            sx={{ width: '135px', '& .MuiInput-input': { fontWeight: 600, color: '#334155' } }}
                        />
                    </Box>
                </Box>

                {isDhOrChc ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                        {/* Type Of Diagnosis */}
                        <Box sx={{ width: { xs: '100%', md: '33.33%' } }}>
                            <Typography sx={{ fontWeight: 700, color: dhDiagnosisData?.diagnosis === 'Normal' ? '#94a3b8' : '#1e293b', fontSize: '14px', mb: 0.5 }}>
                                Type Of Diagnosis {dhDiagnosisData?.diagnosis !== 'Normal' && <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>}
                            </Typography>
                            <FormControl variant="standard" fullWidth error={!!dhErrors?.typeOfDiagnosis} disabled={dhDiagnosisData?.diagnosis === 'Normal'}>
                                <Select
                                    value={dhDiagnosisData?.typeOfDiagnosis || ''}
                                    onChange={(e) => {
                                        setDhDiagnosisData && setDhDiagnosisData(prev => ({ ...prev, typeOfDiagnosis: e.target.value }));
                                        if (dhErrors?.typeOfDiagnosis && setDhErrors) setDhErrors(prev => ({ ...prev, typeOfDiagnosis: null }));
                                    }}
                                >
                                    <MenuItem value="">Select</MenuItem>
                                    <MenuItem value="Provisional">Provisional</MenuItem>
                                    <MenuItem value="Confirmed">Confirmed</MenuItem>
                                </Select>
                                {dhErrors?.typeOfDiagnosis && (
                                    <FormHelperText error sx={{ mt: 0.5, fontSize: '12px' }}>
                                        {dhErrors.typeOfDiagnosis}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        {/* Diagnosis */}
                        <Box sx={{ width: { xs: '100%', md: '33.33%' } }}>
                            <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '14px', mb: 0.5 }}>
                                Diagnosis <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <FormControl variant="standard" fullWidth error={!!dhErrors?.diagnosis}>
                                <Select
                                    value={dhDiagnosisData?.diagnosis || ''}
                                    onChange={(e) => {
                                        const selected = e.target.value;
                                        setDhDiagnosisData && setDhDiagnosisData(prev => ({
                                            ...prev,
                                            diagnosis: selected,
                                            typeOfDiagnosis: selected === 'Normal' ? '' : prev.typeOfDiagnosis
                                        }));
                                        if (dhErrors?.diagnosis && setDhErrors) setDhErrors(prev => ({ ...prev, diagnosis: null }));
                                    }}
                                >
                                    <MenuItem value="">Select</MenuItem>
                                    {!isDiagnosed && <MenuItem value="Normal">Normal</MenuItem>}
                                    <MenuItem value="Chronic cervicitis">Chronic cervicitis</MenuItem>
                                    <MenuItem value="CIN1">CIN1</MenuItem>
                                    <MenuItem value="CIN2">CIN2</MenuItem>
                                    <MenuItem value="CIN3">CIN3</MenuItem>
                                    <MenuItem value="Cervical cancer">Cervical cancer</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>
                                {dhErrors?.diagnosis && (
                                    <FormHelperText error sx={{ mt: 0.5, fontSize: '12px' }}>
                                        {dhErrors.diagnosis}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        {(dhDiagnosisData?.diagnosis === 'Other' || dhDiagnosisData?.diagnosis === 'Others') && (
                            <Box sx={{ width: { xs: '100%', md: '33.33%' } }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label="Other Diagnosis Remarks"
                                    value={dhDiagnosisData?.diagnosisOther || ''}
                                    onChange={(e) => setDhDiagnosisData && setDhDiagnosisData(prev => ({ ...prev, diagnosisOther: e.target.value }))}
                                />
                            </Box>
                        )}

                        {dhDiagnosisData?.diagnosis === 'Cervical cancer' && (
                            <Box sx={{ width: { xs: '100%', md: '33.33%' } }}>
                                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '14px', mb: 0.5 }}>
                                    Stage
                                </Typography>
                                <FormControl variant="standard" fullWidth>
                                    <Select
                                        value={dhDiagnosisData?.cancerStage || ''}
                                        onChange={(e) => setDhDiagnosisData && setDhDiagnosisData(prev => ({ ...prev, cancerStage: e.target.value }))}
                                    >
                                        <MenuItem value="">Select</MenuItem>
                                        <MenuItem value="I">I</MenuItem>
                                        <MenuItem value="II">II</MenuItem>
                                        <MenuItem value="III">III</MenuItem>
                                        <MenuItem value="IV">IV</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                ) : isDiagnosed ? (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1e293b', mb: 2, fontSize: '14px' }}>
                            Please choose an action below to continue
                        </Typography>
                        <RadioGroup
                            row
                            value={diagnosisAction}
                            onChange={(e) => setDiagnosisAction(e.target.value)}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                        <img src="/images/Foloowup.png" alt="Follow Up" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                    </Box>
                                    <FormControlLabel
                                        value="FollowUp"
                                        control={<Radio />}
                                        label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Review / Follow-up</Typography>}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                        <img src="/images/diagnosed.png" alt="Refer" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                    </Box>
                                    <FormControlLabel
                                        value="Refer"
                                        control={<Radio />}
                                        label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Refer</Typography>}
                                    />
                                </Box>
                            </Box>
                        </RadioGroup>
                    </Box>
                ) : (
                    <RadioGroup
                        value={diagnosisAction}
                        onChange={(e) => setDiagnosisAction(e.target.value)}
                    >
                        {/* Recommended Action */}
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontWeight: 800, color: '#1e293b', mb: 1.5, fontSize: '14px' }}>
                                Recommended action
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {isNad && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '45px', height: '45px', borderRadius: '50%', overflow: 'hidden' }}>
                                            <img src="/images/no-visible-symptom.png" alt="NAD" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
                                        </Box>
                                        <FormControlLabel
                                            value="NAD"
                                            control={<Radio />}
                                            label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>No Abnormality Found (NAD)</Typography>}
                                        />
                                    </Box>
                                )}

                                {cryotherapyRecommended && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '45px', height: '45px', borderRadius: '50%', overflow: 'hidden' }}>
                                            <img src="/images/no-visible-symptom.png" alt="Cryotherapy" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
                                        </Box>
                                        <FormControlLabel
                                            value="CRYOTHERAPY"
                                            control={<Radio />}
                                            label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Cryotherapy (recommended)</Typography>}
                                        />
                                    </Box>
                                )}

                                {managementRecommended && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                            <img src="/images/Foloowup.png" alt="Management" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                        </Box>
                                        <FormControlLabel
                                            value="Management"
                                            control={<Radio />}
                                            label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Management / Review</Typography>}
                                        />
                                    </Box>
                                )}

                                {referralRecommended && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                            <img src="/images/diagnosed.png" alt="Refer" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                        </Box>
                                        <FormControlLabel
                                            value="Refer"
                                            control={<Radio />}
                                            label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Refer</Typography>}
                                        />
                                    </Box>
                                )}

                                {!isNad && !cryotherapyRecommended && !managementRecommended && !referralRecommended && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                            <img src="/images/Foloowup.png" alt="Management" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                        </Box>
                                        <FormControlLabel
                                            value="Management"
                                            control={<Radio />}
                                            label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Management (recommended)</Typography>}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Override Options - ONLY shown when !referralRecommended */}
                        {!referralRecommended && (
                            <Box sx={{ mb: 4 }}>
                                <Typography sx={{ fontWeight: 800, color: '#1e293b', mb: 1.5, fontSize: '14px' }}>
                                    You can override the recommended action by choosing one of the following options
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                        <img src="/images/diagnosed.png" alt="Refer" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                    </Box>
                                    <FormControlLabel
                                        value="Refer"
                                        control={<Radio />}
                                        label={<Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>Refer</Typography>}
                                    />
                                </Box>
                            </Box>
                        )}
                    </RadioGroup>
                )}

                {/* Doctor's Observation */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 800, color: '#1e293b', mb: 1.5, fontSize: '14px' }}>
                        Doctor's Observation
                    </Typography>
                    <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Enter Doctor's Observation"
                        value={doctorsObservation}
                        onChange={(e) => setDoctorsObservation(e.target.value)}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: '#3f51b5', borderBottomWidth: '2px' },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#3f51b5', borderBottomWidth: '2px' },
                            '& .MuiInput-underline:after': { borderBottomColor: '#3f51b5', borderBottomWidth: '2px' }
                        }}
                    />
                </Box>
            </Box>

            {/* Save & Continue Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    onClick={handleSaveDiagnosis}
                    variant="contained"
                    sx={{
                        bgcolor: '#3f51b5',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 4.5,
                        py: 1,
                        borderRadius: '4px',
                        '&:hover': { bgcolor: '#303f9f' }
                    }}
                >
                    Save & Continue
                </Button>
            </Box>
        </Box>
    );
};

export default CervicalDiagnosisTab;
